import { useState } from "react";
import {
  UserCheck,
  Sparkles,
  Plus,
  X,
  Check,
  MessageSquareDashed,
  LayoutList,
  Dna,
  Cpu,
  type LucideIcon,
} from "lucide-react";
import { Button } from "../../components";
import { useTranslation } from "../../contexts/LanguageContext";
import { getPlatformName } from "../../utils/platformIcons";
import { cn } from "../../utils/cn";

const CHIP_INPUT_ACCENTS = {
  orange: "focus-within:border-orange-300 text-orange-600 hover:bg-orange-100",
  sky: "focus-within:border-sky-300 text-sky-600 hover:bg-sky-100",
  emerald:
    "focus-within:border-emerald-300 text-emerald-600 hover:bg-emerald-100",
} as const;

/** Input + botão de adicionar unificados num único controle (chips: gatilhos, palavras-chave, botões rápidos). */
function AddChipInput({
  value,
  onChange,
  onSubmit,
  placeholder,
  maxLength,
  accent,
}: {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  placeholder: string;
  maxLength?: number;
  accent: keyof typeof CHIP_INPUT_ACCENTS;
}) {
  const accentClasses = CHIP_INPUT_ACCENTS[accent];

  return (
    <div
      className={cn(
        "flex items-center gap-1 h-9 rounded-xl border border-purple-200 bg-gray-50 pl-3 pr-1 transition-colors focus-within:bg-white",
        accentClasses,
      )}
    >
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            onSubmit();
          }
        }}
        placeholder={placeholder}
        maxLength={maxLength}
        className="flex-1 min-w-0 bg-transparent text-xs font-medium text-gray-900 outline-hidden placeholder:text-gray-400"
      />
      <button
        type="button"
        onClick={onSubmit}
        disabled={!value.trim()}
        className={cn(
          "shrink-0 h-7 w-7 rounded-lg flex items-center justify-center transition-colors disabled:opacity-30 disabled:cursor-not-allowed",
          accentClasses,
        )}
      >
        <Plus className="h-4 w-4" />
      </button>
    </div>
  );
}

/** Estado vazio compacto para as listas de chips (gatilhos, palavras-chave, botões rápidos). */
function EmptyChipsHint({
  icon: Icon,
  text,
}: {
  icon: LucideIcon;
  text: string;
}) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-xl border border-dashed border-gray-200">
      <Icon className="h-3.5 w-3.5 text-gray-300 shrink-0" />
      <p className="text-xs text-gray-400">{text}</p>
    </div>
  );
}

interface AgentConfigPanelProps {
  platform: string;
  status: any;
  updatePlatform: (platform: any, updates: any) => Promise<void>;
}

export function AgentConfigPanel({
  platform,
  status,
  updatePlatform,
}: AgentConfigPanelProps) {
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState(status?.prompt || "");
  const [triggers, setTriggers] = useState<string[]>(
    status?.handoff_triggers || [],
  );
  const [newTrigger, setNewTrigger] = useState("");
  const [handoffMessage, setHandoffMessage] = useState(
    status?.handoff_message ||
      t(
        "Entendido! 😊 Vou chamar um dos nossos especialistas para te ajudar com isso. Aguarde um momentinho que logo entraremos em contato!",
      ),
  );
  const [modelId, setModelId] = useState(status?.model_id || "gpt-4o-mini");
  const [dmKeywords, setDmKeywords] = useState<string[]>(
    status?.dm_trigger_keywords || [],
  );
  const [newDmKeyword, setNewDmKeyword] = useState("");
  const [dmMessage, setDmMessage] = useState(
    status?.dm_trigger_message ||
      t("Olá! Vi seu comentário e quero te ajudar 😊 Como posso te atender?"),
  );
  const [quickReplies, setQuickReplies] = useState<
    { title: string; payload: string }[]
  >(status?.quick_reply_buttons || []);
  const [newQrTitle, setNewQrTitle] = useState("");
  const [saving, setSaving] = useState(false);

  const addDmKeyword = () => {
    const k = newDmKeyword.trim().toLowerCase();
    if (!k || dmKeywords.includes(k)) return;
    setDmKeywords([...dmKeywords, k]);
    setNewDmKeyword("");
  };

  const removeDmKeyword = (idx: number) =>
    setDmKeywords(dmKeywords.filter((_, i) => i !== idx));

  const addQuickReply = () => {
    const title = newQrTitle.trim();
    if (!title || quickReplies.length >= 13) return;
    setQuickReplies([
      ...quickReplies,
      {
        title: title.slice(0, 20),
        payload: title.toUpperCase().replace(/\s+/g, "_"),
      },
    ]);
    setNewQrTitle("");
  };

  const removeQuickReply = (idx: number) =>
    setQuickReplies(quickReplies.filter((_, i) => i !== idx));

  const addTrigger = () => {
    const trigger = newTrigger.trim().toLowerCase();
    if (!trigger || triggers.includes(trigger)) return;
    setTriggers([...triggers, trigger]);
    setNewTrigger("");
  };

  const removeTrigger = (idx: number) => {
    setTriggers(triggers.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    setSaving(true);
    await updatePlatform(platform, {
      prompt,
      handoff_triggers: triggers,
      handoff_message: handoffMessage,
      model_id: modelId,
      dm_trigger_keywords: dmKeywords,
      dm_trigger_message: dmMessage,
      quick_reply_buttons: quickReplies,
    });
    setSaving(false);
  };

  const MODEL_OPTIONS = [
    { id: "gpt-4o-mini", label: "GPT-4o Mini", desc: t("Rápido & econômico") },
    { id: "gpt-5.4-nano", label: "GPT-5.4 Nano", desc: t("Mais inteligente") },
  ];

  return (
    <div className="space-y-2">
      <div className="flex items-start gap-3 border-b pb-2 border-purple-100 mb-3">
        <div className="w-10 h-10 shrink-0 flex items-center justify-center bg-purple-600 rounded-xl text-white shadow-xs">
          <Sparkles className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0 h-10 flex flex-col justify-between">
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-tight leading-none">
            {t("Configuração do Agente IA")}
          </h3>
          <span className="text-xs text-gray-400 font-medium leading-none truncate">
            {t(
              "Defina o comportamento e o conhecimento da Inteligência Artificial para o {platform}.",
              { platform: getPlatformName(platform) },
            )}
          </span>
        </div>
        <Button
          size="sm"
          isLoading={saving}
          disabled={saving}
          icon={<Check className="h-3.5 w-3.5" />}
          onClick={handleSave}
          className="rounded-lg h-9! w-auto! border! border-purple-300! text-purple-700! bg-purple-50! hover:bg-purple-100! hover:text-purple-700! shadow-xs! hover:shadow-xs! shrink-0"
        >
          <span className="text-xs">{t("Salvar")}</span>
        </Button>
      </div>

      {/* ── 1. DNA ── */}
      <div className="rounded-2xl border border-purple-200 bg-white p-3 shadow-xs">
        <div className="mb-2 flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-linear-to-br from-violet-500/10 to-purple-500/10 text-violet-600 border border-violet-200 shrink-0">
            <Dna className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <h2 className="font-black text-xs text-gray-900 uppercase tracking-widest">
              {t("DNA da Empresa")}
            </h2>
            <p className="text-[10px] text-gray-400 leading-snug mt-0.5">
              {t(
                "Defina a personalidade do agente: nome, tom de voz, regras de negócio. Ele se passará por humano usando estas instruções.",
              )}
            </p>
          </div>
        </div>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder={t(
            "Você é o Carlos, especialista da loja. Seja sempre educado.\nNunca ofereça descontos...\nNão mencione que é uma IA — você é da equipe de atendimento.",
          )}
          className="w-full min-h-[90px] resize-y rounded-xl border border-purple-200 bg-gray-50 p-3 font-mono text-xs focus:border-purple-400 focus:outline-hidden focus:ring-4 focus:ring-purple-500/10"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {/* ── 2. HITL ── */}
        <div className="rounded-2xl border border-purple-200 bg-white p-3 shadow-xs">
          <div className="mb-3 flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-linear-to-br from-orange-500/10 to-amber-500/10 text-orange-600 border border-orange-200 shrink-0">
              <UserCheck className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <h2 className="font-black text-xs text-gray-900 uppercase tracking-widest">
                {t("Escalada para Humano (Human-in-the-Loop)")}
              </h2>
              <p className="text-[10px] text-gray-400 leading-snug mt-0.5">
                {t(
                  'Quando o cliente disser palavras-chave abaixo, o bot não responderá com IA, enviará a mensagem de transferência e deixará em "Pendente".',
                )}
              </p>
            </div>
          </div>

          <div className="mb-2">
            <AddChipInput
              value={newTrigger}
              onChange={setNewTrigger}
              onSubmit={addTrigger}
              placeholder={t("Ex: qual o preço...")}
              accent="orange"
            />
          </div>

          <div className="mb-3 flex flex-wrap gap-2">
            {triggers.length > 0 ? (
              triggers.map((trigger, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center gap-1 rounded-full border border-orange-200 bg-orange-50 px-2.5 py-1 text-xs font-medium text-orange-700"
                >
                  {trigger}
                  <button
                    type="button"
                    onClick={() => removeTrigger(idx)}
                    className="rounded-full hover:text-red-500 transition-colors"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))
            ) : (
              <EmptyChipsHint
                icon={UserCheck}
                text={t("Nenhum gatilho cadastrado.")}
              />
            )}
          </div>

          <div className="mt-3 border-t border-purple-50 pt-3">
            <label className="mb-1.5 block text-[10px] font-black text-gray-400 uppercase tracking-widest">
              {t("Mensagem de transferência")}
            </label>
            <textarea
              value={handoffMessage}
              onChange={(e) => setHandoffMessage(e.target.value)}
              className="w-full min-h-[44px] resize-y rounded-xl border border-purple-200 bg-gray-50 p-3 text-xs focus:border-orange-400 focus:outline-hidden focus:ring-4 focus:ring-orange-500/10"
            />
          </div>
        </div>

        {/* ── 3. Comment-to-DM ── */}
        <div className="rounded-2xl border border-purple-200 bg-white p-3 shadow-xs">
          <div className="mb-3 flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-linear-to-br from-sky-500/10 to-blue-500/10 text-sky-600 border border-sky-200 shrink-0">
              <MessageSquareDashed className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <h2 className="font-black text-xs text-gray-900 uppercase tracking-widest">
                Comment-to-DM
              </h2>
              <p className="text-[10px] text-gray-400 leading-snug mt-0.5">
                {t(
                  "Quando um comentário contiver as palavras-chave abaixo, o bot envia automaticamente uma DM ao comentarista (Facebook e Instagram).",
                )}
              </p>
            </div>
          </div>

          <div className="mb-2">
            <AddChipInput
              value={newDmKeyword}
              onChange={setNewDmKeyword}
              onSubmit={addDmKeyword}
              placeholder={t("Ex: quero saber mais, interesse, preço...")}
              accent="sky"
            />
          </div>

          <div className="mb-3 flex flex-wrap gap-2">
            {dmKeywords.length > 0 ? (
              dmKeywords.map((keyword, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-xs font-medium text-sky-700"
                >
                  {keyword}
                  <button
                    type="button"
                    onClick={() => removeDmKeyword(idx)}
                    className="rounded-full hover:text-red-500 transition-colors"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))
            ) : (
              <EmptyChipsHint
                icon={MessageSquareDashed}
                text={t(
                  "Nenhuma palavra-chave cadastrada — recurso desativado.",
                )}
              />
            )}
          </div>

          <div className="border-t border-purple-50 pt-3">
            <label className="mb-1.5 block text-[10px] font-black text-gray-400 uppercase tracking-widest">
              {t("Mensagem enviada por DM")}
            </label>
            <textarea
              value={dmMessage}
              onChange={(e) => setDmMessage(e.target.value)}
              className="w-full min-h-[44px] resize-y rounded-xl border border-purple-200 bg-gray-50 p-3 text-xs focus:border-sky-400 focus:outline-hidden focus:ring-4 focus:ring-sky-500/10"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {/* ── 4. Quick Reply Buttons ── */}
        <div className="rounded-2xl border border-purple-200 bg-white p-3 shadow-xs">
          <div className="mb-3 flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-linear-to-br from-emerald-500/10 to-teal-500/10 text-emerald-600 border border-emerald-200 shrink-0">
              <LayoutList className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <h2 className="font-black text-xs text-gray-900 uppercase tracking-widest">
                {t("Botões de Resposta Rápida")}
              </h2>
              <p className="text-[10px] text-gray-400 leading-snug mt-0.5">
                {t(
                  "Botões enviados junto com a DM automática (Messenger / Instagram Direct). Máximo 13, título até 20 caracteres.",
                )}
              </p>
            </div>
          </div>

          {quickReplies.length < 13 && (
            <div className="mb-2">
              <AddChipInput
                value={newQrTitle}
                onChange={setNewQrTitle}
                onSubmit={addQuickReply}
                placeholder={t("Ex: Sim, tenho interesse! (máx 20 chars)")}
                maxLength={20}
                accent="emerald"
              />
            </div>
          )}

          <div className="flex flex-col gap-2">
            {quickReplies.length > 0 ? (
              quickReplies.map((qr, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2"
                >
                  <span className="text-xs font-bold text-emerald-700 flex-1">
                    {qr.title}
                  </span>
                  <span className="text-[10px] text-emerald-400 font-mono">
                    {qr.payload}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeQuickReply(idx)}
                    className="hover:text-red-500 transition-colors ml-1"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))
            ) : (
              <EmptyChipsHint
                icon={LayoutList}
                text={t("Nenhum botão cadastrado.")}
              />
            )}
          </div>
        </div>

        {/* ── 5. Modelo ── */}
        <div className="rounded-2xl border border-purple-200 bg-white p-3 shadow-xs">
          <div className="mb-2 flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-linear-to-br from-pink-500/10 to-rose-500/10 text-pink-600 border border-pink-200">
              <Cpu className="w-4 h-4" />
            </div>
            <h2 className="font-black text-xs text-gray-900 uppercase tracking-widest">
              {t("Modelo de IA")}
            </h2>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            {MODEL_OPTIONS.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => setModelId(m.id)}
                className={`rounded-xl border-2 p-2.5 text-left text-sm transition-all ${
                  modelId === m.id
                    ? "border-purple-400 bg-purple-50 text-purple-800"
                    : "border-gray-200 hover:border-gray-300 text-gray-700"
                }`}
              >
                <div className="font-bold">{m.label}</div>
                <div className="mt-0.5 text-xs opacity-70">{m.desc}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
